import { isTextNode, normalizeClassList } from './htmlEditorUtils.js';

export const EDITOR_MODES = Object.freeze({
  SIMPLE: 'simple',
  ADVANCED: 'advanced',
});

const SIMPLE_CONTENT_TAGS = new Set([
  'a', 'audio', 'blockquote', 'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'img', 'li', 'ol', 'p', 'table', 'ul', 'video',
]);

const SIMPLE_SECTION_TAGS = new Set([
  'article', 'figure', 'footer', 'header', 'main', 'projects', 'section', 'skills',
]);

const SIMPLE_COMPONENT_CLASSES = new Set([
  'accordion', 'alert', 'card', 'carousel', 'list-group', 'progress',
]);

export const ADVANCED_FEATURE_LABELS = Object.freeze({
  nestedLayout: 'nested layout containers',
});

const MAX_SIMPLE_CONTAINER_DEPTH = 3;

const PRESET_CLASS_GROUPS = Object.freeze({
  alignment: ['text-start', 'text-center', 'text-end'],
  spacing: ['py-0', 'py-2', 'py-4', 'py-5'],
  tone: [
    'bg-light', 'bg-dark', 'bg-primary', 'bg-secondary',
    'text-dark', 'text-white',
  ],
  border: ['border'],
  rounded: ['rounded'],
  shadow: ['shadow'],
  imageShape: ['img-fluid', 'img-thumbnail', 'rounded', 'rounded-circle'],
  width: ['w-auto', 'w-25', 'w-50', 'w-75', 'w-100'],
  emphasis: ['fw-normal', 'fw-semibold', 'fw-bold'],
});

export const SIMPLE_APPEARANCE_OPTIONS = Object.freeze({
  alignment: [
    { value: '', label: 'Default', description: 'Follow the page layout', iconClass: 'bi-distribute-horizontal' },
    { value: 'text-start', label: 'Left', description: 'Align content left', iconClass: 'bi-text-left' },
    { value: 'text-center', label: 'Centre', description: 'Align content centrally', iconClass: 'bi-text-center' },
    { value: 'text-end', label: 'Right', description: 'Align content right', iconClass: 'bi-text-right' },
  ],
  spacing: [
    { value: 'py-0', label: 'None', description: 'No extra breathing room', iconClass: 'bi-arrows-collapse-vertical' },
    { value: 'py-2', label: 'Compact', description: 'A small amount of space', iconClass: 'bi-distribute-vertical' },
    { value: 'py-4', label: 'Comfortable', description: 'Balanced spacing', iconClass: 'bi-arrows-expand-vertical' },
    { value: 'py-5', label: 'Spacious', description: 'Generous breathing room', iconClass: 'bi-arrows-fullscreen' },
  ],
  tone: [
    { value: '', label: 'Plain', description: 'Use the page background', classes: [], previewClass: 'plain' },
    { value: 'light', label: 'Light', description: 'Soft and understated', classes: ['bg-light', 'text-dark'], previewClass: 'light' },
    { value: 'dark', label: 'Dark', description: 'Strong dark contrast', classes: ['bg-dark', 'text-white'], previewClass: 'dark' },
    { value: 'primary', label: 'Primary', description: 'Use the main accent colour', classes: ['bg-primary', 'text-white'], previewClass: 'primary' },
    { value: 'secondary', label: 'Secondary', description: 'Use a quieter accent', classes: ['bg-secondary', 'text-white'], previewClass: 'secondary' },
  ],
  imageShape: [
    { value: 'img-fluid', label: 'Natural', description: 'Keep the original shape', classes: ['img-fluid'], iconClass: 'bi-image' },
    { value: 'rounded', label: 'Rounded', description: 'Gently rounded corners', classes: ['img-fluid', 'rounded'], iconClass: 'bi-square' },
    { value: 'rounded-circle', label: 'Circular', description: 'Crop into a circle', classes: ['img-fluid', 'rounded-circle'], iconClass: 'bi-circle' },
    { value: 'img-thumbnail', label: 'Framed', description: 'Add a light image frame', classes: ['img-thumbnail'], iconClass: 'bi-card-image' },
  ],
  width: [
    { value: 'w-auto', label: 'Natural', description: 'Fit the content', iconClass: 'bi-arrows' },
    { value: 'w-25', label: 'Small', description: 'About one quarter wide', iconClass: 'bi-layout-sidebar-inset' },
    { value: 'w-50', label: 'Medium', description: 'About half width', iconClass: 'bi-layout-split' },
    { value: 'w-75', label: 'Large', description: 'About three quarters wide', iconClass: 'bi-layout-sidebar-inset-reverse' },
    { value: 'w-100', label: 'Full width', description: 'Fill the available width', iconClass: 'bi-arrows-expand' },
  ],
  emphasis: [
    { value: '', label: 'Default', description: 'Follow the page style', iconClass: 'bi-type' },
    { value: 'fw-normal', label: 'Regular', description: 'Normal text weight', iconClass: 'bi-type' },
    { value: 'fw-semibold', label: 'Emphasised', description: 'A little more prominent', iconClass: 'bi-type-bold' },
    { value: 'fw-bold', label: 'Bold', description: 'Strongly emphasised', iconClass: 'bi-type-bold' },
  ],
});

const nodeClasses = (node) => normalizeClassList(node?.attributes?.class);

export const isSimpleSectionNode = (node) => {
  if (!node || isTextNode(node)) return false;
  if (SIMPLE_SECTION_TAGS.has(node.type)) return true;
  return nodeClasses(node).some((className) => SIMPLE_COMPONENT_CLASSES.has(className));
};

export const isSimpleSelectableNode = (node) => {
  if (!node || isTextNode(node)) return false;

  const managedRole = node.attributes?.['data-managed-role'];
  if (managedRole && !['projects-collection', 'skills-collection'].includes(managedRole)) {
    return false;
  }

  return SIMPLE_CONTENT_TAGS.has(node.type) || isSimpleSectionNode(node);
};

export const detectAdvancedModeFeatures = (editorDocument) => {
  const detected = new Set();

  const visit = (node, containerDepth = 0, isRoot = false) => {
    if (!node || isTextNode(node)) return;

    // Project collections can contain intentionally deep generated markup.
    // Their records are edited through the collection controls in Simple mode.
    if (node.type === 'projects') return;

    const attributes = node.attributes || {};
    const managed = Boolean(
      attributes['data-managed-role']
      || attributes['data-project-field']
      || attributes['data-skill-field']
    );
    if (managed) return;

    const isContainer = !isRoot && (
      node.type === 'div'
      || SIMPLE_SECTION_TAGS.has(node.type)
    );
    const nextDepth = isContainer ? containerDepth + 1 : 0;
    if (nextDepth > MAX_SIMPLE_CONTAINER_DEPTH) {
      detected.add('nestedLayout');
    }

    (node.children || []).forEach((child) => visit(child, nextDepth, false));
  };

  visit(editorDocument?.root, 0, true);
  const features = [...detected];
  return {
    hasAdvancedFeatures: features.length > 0,
    features,
    labels: features.map((feature) => ADVANCED_FEATURE_LABELS[feature]),
  };
};

const findElementPath = (node, nodeId, path = []) => {
  if (!node || isTextNode(node)) return null;
  const nextPath = [...path, node];
  if (node.id === nodeId) return nextPath;

  for (const child of node.children || []) {
    const match = findElementPath(child, nodeId, nextPath);
    if (match) return match;
  }

  return null;
};

const collectSimpleChildren = (node) => {
  const result = [];

  const visit = (child) => {
    if (!child || isTextNode(child)) return;
    if (isSimpleSelectableNode(child)) {
      result.push(child);
      return;
    }
    (child.children || []).forEach(visit);
  };

  (node?.children || []).forEach(visit);
  return result;
};

export const resolveSimpleSelectionId = (root, nodeId) => {
  const path = findElementPath(root, nodeId);
  if (!path) return null;

  for (let index = path.length - 1; index > 0; index -= 1) {
    if (isSimpleSelectableNode(path[index])) return path[index].id;
  }

  return null;
};

export const getSimpleSelectionContext = (root, nodeId) => {
  const emptyContext = {
    path: [],
    parentId: null,
    previousSiblingId: null,
    nextSiblingId: null,
    firstChildId: null,
  };
  const resolvedId = resolveSimpleSelectionId(root, nodeId);
  const fullPath = findElementPath(root, resolvedId);
  if (!fullPath || fullPath.length < 2) return emptyContext;

  const selectedNode = fullPath[fullPath.length - 1];
  const simplePath = [fullPath[0], ...fullPath.slice(1).filter(isSimpleSelectableNode)];
  const simpleParent = simplePath[simplePath.length - 2] || fullPath[0];
  const siblings = collectSimpleChildren(simpleParent);
  const siblingIndex = siblings.findIndex((node) => node.id === selectedNode.id);
  const children = collectSimpleChildren(selectedNode);

  return {
    path: simplePath,
    parentId: simplePath.length > 2 ? simpleParent.id : null,
    previousSiblingId: siblingIndex > 0 ? siblings[siblingIndex - 1].id : null,
    nextSiblingId: siblingIndex >= 0 && siblingIndex < siblings.length - 1
      ? siblings[siblingIndex + 1].id
      : null,
    firstChildId: children[0]?.id || null,
  };
};

const classesForPreset = (group, value) => {
  const option = SIMPLE_APPEARANCE_OPTIONS[group]?.find((entry) => entry.value === value);
  if (option?.classes) return option.classes;
  return value ? [value] : [];
};

export const applySimpleAppearancePreset = (node, group, value) => {
  if (!node || isTextNode(node) || !PRESET_CLASS_GROUPS[group]) return node;

  const removeClasses = new Set(PRESET_CLASS_GROUPS[group]);
  const retainedClasses = nodeClasses(node).filter((className) => !removeClasses.has(className));
  const classes = normalizeClassList([...retainedClasses, ...classesForPreset(group, value)]);

  return {
    ...node,
    attributes: {
      ...(node.attributes || {}),
      class: classes,
    },
  };
};

export const getSimpleAppearanceValue = (node, group) => {
  const classes = nodeClasses(node);
  const options = SIMPLE_APPEARANCE_OPTIONS[group] || [];
  const match = options.find((option) => {
    const expected = option.classes || (option.value ? [option.value] : []);
    return expected.length > 0 && expected.every((className) => classes.includes(className));
  });
  return match?.value || '';
};
